import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, RefreshCw, Loader2, Eye, CheckCircle2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { servers, products as productsApi, customers as customersApi } from '../api';
import type { StackKandidat } from '../api';

/**
 * Fremde Compose-Stacks als beobachtete Instanzen aufnehmen.
 *
 * Das Backend konnte das seit Langem (`GET /servers/:id/stacks`,
 * `POST …/adopt`), nur gab es keinen Weg dorthin in der Oberfläche — wer einen
 * Portainer-Stack in die Übersicht holen wollte, fand im Servermenü nichts.
 * Aufgefallen am 09.10.2026 beim eLibrary-Prototyp auf kamino.
 *
 * Nichts hier fasst den Host an: die Liste liest `docker ps`, die Aufnahme legt
 * eine Zeile an. Für Produkte, die es noch nicht gibt, lässt sich direkt ein
 * Betriebsprodukt anlegen — Entwurf, nicht im Verkauf, also auch nicht in
 * WooCommerce oder Magento.
 */
export default function StackAufnahmePanel({
  serverId,
  hostname,
  onClose,
}: {
  serverId: string;
  hostname: string;
  onClose: () => void;
}) {
  const liste = useQuery({
    queryKey: ['servers', serverId, 'stacks'],
    queryFn: () => servers.stacks(serverId),
  });
  const produkte = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list() });
  const kunden = useQuery({ queryKey: ['customers', 'alle'], queryFn: () => customersApi.list() });

  const offen = liste.data?.filter((k) => !k.erfasst) ?? [];
  const erfasst = liste.data?.filter((k) => k.erfasst) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
              <Eye size={18} /> Fremde Stacks aufnehmen
            </h2>
            <p className="text-xs text-slate-500">
              {hostname} · Compose-Projekte, die nicht vom PC ausgerollt wurden (z. B. über Portainer).
              Aufgenommen werden sie als beobachtete Instanz: sichtbar, aber nicht steuerbar.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => liste.refetch()}
              disabled={liste.isFetching}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-40"
              title="Neu einlesen"
            >
              <RefreshCw size={16} className={liste.isFetching ? 'animate-spin' : ''} />
            </button>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Schließen">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
          {liste.isLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Liest die Container auf {hostname} …
            </div>
          )}
          {liste.isError && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {(liste.error as Error).message}
            </div>
          )}

          {offen.map((k) => (
            <KandidatZeile
              key={k.projekt}
              k={k}
              serverId={serverId}
              produkte={produkte.data ?? []}
              kunden={kunden.data ?? []}
            />
          ))}
          {liste.data && !offen.length && (
            <p className="text-sm text-slate-500">Alle Compose-Projekte auf diesem Host sind schon erfasst.</p>
          )}

          {erfasst.length > 0 && (
            <div className="pt-2">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Schon erfasst</div>
              <div className="flex flex-wrap gap-1.5">
                {erfasst.map((k) => (
                  <span key={k.projekt} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{k.projekt}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KandidatZeile({
  k,
  serverId,
  produkte,
  kunden,
}: {
  k: StackKandidat;
  serverId: string;
  produkte: { id: string; slug: string; name: string }[];
  kunden: { id: string; companyName: string }[];
}) {
  const qc = useQueryClient();
  const vorschlag = produkte.find((p) => p.slug === k.vermutetesProdukt)?.id ?? '';
  const [productId, setProductId] = useState(vorschlag);
  const [customerId, setCustomerId] = useState('');
  const [domain, setDomain] = useState('');
  const [neuesProdukt, setNeuesProdukt] = useState(false);
  const [produktName, setProduktName] = useState('');
  const [produktSlug, setProduktSlug] = useState('');
  const [aufgenommen, setAufgenommen] = useState<string | null>(null);

  const produktAnlegen = useMutation({
    mutationFn: () =>
      productsApi.create({ name: produktName.trim(), slug: produktSlug.trim(), status: 'DRAFT', imVerkauf: false }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['products'] });
      setProductId(p.id);
      setNeuesProdukt(false);
    },
  });

  const aufnehmen = useMutation({
    mutationFn: () =>
      servers.stackAufnehmen(serverId, k.projekt, { productId, customerId, domain: domain.trim() || null }),
    onSuccess: (inst) => {
      setAufgenommen(inst.id);
      qc.invalidateQueries({ queryKey: ['servers', serverId, 'stacks'] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  const fehler = (aufnehmen.error ?? produktAnlegen.error) as Error | null;

  return (
    <div className="rounded-lg border border-slate-200 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono text-sm font-semibold text-slate-800">{k.projekt}</span>
        <span className="text-xs text-slate-500">{k.laufend} von {k.container} Containern laufen</span>
      </div>

      {aufgenommen ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-700">
          <CheckCircle2 size={15} /> Aufgenommen.{' '}
          <Link to={`/instances/${aufgenommen}`} className="font-medium underline">Zur Instanz</Link>
        </p>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <label className="text-xs text-slate-600">
            Produkt
            {neuesProdukt ? (
              <div className="mt-1 space-y-1">
                <input value={produktName} onChange={(e) => setProduktName(e.target.value)} placeholder="Name, z. B. eLibrary (Prototyp)" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                <input value={produktSlug} onChange={(e) => setProduktSlug(e.target.value.toLowerCase())} placeholder="slug, z. B. elibrary" className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-sm" />
                <div className="flex gap-1">
                  <button type="button" onClick={() => produktAnlegen.mutate()} disabled={!produktName.trim() || !/^[a-z0-9-]+$/.test(produktSlug) || produktAnlegen.isPending} className="rounded bg-brand-600 px-2 py-1 text-xs text-white disabled:opacity-40">
                    Anlegen
                  </button>
                  <button type="button" onClick={() => setNeuesProdukt(false)} className="rounded px-2 py-1 text-xs text-slate-500">Abbrechen</button>
                </div>
                <p className="text-[10px] text-slate-400">Entwurf, nicht im Verkauf — erscheint in keinem Shop.</p>
              </div>
            ) : (
              <div className="mt-1 flex gap-1">
                <select value={productId} onChange={(e) => setProductId(e.target.value)} className="w-full rounded border border-slate-300 px-2 py-1 text-sm">
                  <option value="">— wählen —</option>
                  {produkte.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button type="button" onClick={() => setNeuesProdukt(true)} className="rounded border border-slate-300 px-1.5 text-slate-500 hover:bg-slate-50" title="Neues Betriebsprodukt anlegen">
                  <Plus size={14} />
                </button>
              </div>
            )}
          </label>
          <label className="text-xs text-slate-600">
            Kunde
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm">
              <option value="">— wählen —</option>
              {kunden.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            Domain (nur Anzeige und TLS)
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="z. B. dev-elibrary.juni.cloud" className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
          <div className="sm:col-span-3 flex items-center justify-end gap-2">
            {fehler && <span className="text-xs text-red-700">{fehler.message}</span>}
            <button
              type="button"
              onClick={() => aufnehmen.mutate()}
              disabled={!productId || !customerId || aufnehmen.isPending}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
            >
              {aufnehmen.isPending ? 'Nimmt auf …' : 'Als beobachtete Instanz aufnehmen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
